import {
  ArrowRight,
  Award,
  BookOpen,
  Building2,
  CalendarCheck,
  Clock,
  CheckCircle2,
  CreditCard,
  GraduationCap,
  MapPin,
  Medal,
  Menu,
  Phone,
  Quote,
  Star,
  Stethoscope,
  Users,
} from "lucide-react";

const whatsappUrl =
  "https://wa.me/5521991405512?text=Ol%C3%A1%2C%20quero%20me%20inscrever%20na%20P%C3%B3s-gradua%C3%A7%C3%A3o%20M%C3%A9dica%20em%20Acupuntura.";

const navItems = [
  { label: "SOBRE O CURSO", href: "#sobre" },
  { label: "ESTRUTURA", href: "#estrutura" },
  { label: "COORDENAÇÃO", href: "#coordenacao" },
  { label: "DEPOIMENTOS", href: "#depoimentos" },
  { label: "INFORMAÇÕES", href: "#informacoes" },
];

const highlights = [
  { icon: GraduationCap, title: "600 HORAS", text: "DE FORMAÇÃO" },
  { icon: Stethoscope, title: "300 HORAS", text: "DE PRÁTICA SUPERVISIONADA" },
  { icon: BookOpen, title: "EMBASAMENTO", text: "CIENTÍFICO E EVIDÊNCIAS CLÍNICAS" },
  { icon: Users, title: "ATENDIMENTO", text: "A PACIENTES REAIS" },
];

const courseCards = [
  {
    icon: GraduationCap,
    title: "CARGA HORÁRIA",
    text: "600 horas de formação, sendo 300 horas de prática supervisionada.",
  },
  {
    icon: BookOpen,
    title: "EMBASAMENTO CIENTÍFICO",
    text: "Conteúdo atualizado com evidências clínicas e base na Medicina Chinesa.",
  },
  {
    icon: Users,
    title: "ATENDIMENTO REAL",
    text: "Experiência prática com pacientes, desenvolvendo segurança e resultados.",
  },
  {
    icon: Award,
    title: "CERTIFICAÇÃO",
    text: "Certificado de Pós-graduação reconhecido e valorizado no mercado médico.",
  },
];

const courseInfo = [
  {
    icon: CalendarCheck,
    title: "DURAÇÃO",
    text: "24 meses ou 2 anos de pós-graduação médica em Acupuntura.",
  },
  {
    icon: Clock,
    title: "CARGA HORÁRIA",
    text: "600 horas totais, com 300 horas de prática ambulatorial e 300 horas de aula teórica.",
  },
  {
    icon: Stethoscope,
    title: "PRÁTICA AMBULATORIAL",
    text: "Ambulatório de 4 horas por semana para atendimento e vivência supervisionada.",
  },
  {
    icon: BookOpen,
    title: "AULAS TEÓRICAS",
    text: "Aulas on-line ou presenciais, aos sábados, 2 vezes por mês, das 9h às 18h.",
  },
];

const investmentInfo = [
  { label: "MATRÍCULA", value: "R$ 450,00" },
  { label: "24 PARCELAS", value: "R$ 990,00" },
];

const testimonials = [
  {
    quote:
      "O curso superou minhas expectativas. A combinação de teoria e prática faz toda a diferença na rotina clínica.",
    name: "Dr. Ricardo A.",
    role: "Médico Acupunturista",
  },
  {
    quote:
      "Excelência no ensino, professores incríveis e a estrutura da Santa Casa é incomparável.",
    name: "Dra. Juliana M.",
    role: "Clínica Geral",
  },
  {
    quote:
      "Consegui aplicar os conhecimentos desde o início e os resultados nos pacientes foram surpreendentes.",
    name: "Dr. Paulo S.",
    role: "Reumatologista",
  },
];

function CtaButton({ children, className = "" }: { children: string; className?: string }) {
  return (
    <a className={`site-cta ${className}`} href={whatsappUrl} target="_blank" rel="noreferrer">
      <span>{children}</span>
      <ArrowRight size={22} strokeWidth={2.8} />
    </a>
  );
}

function LogoBlock({ light = false }: { light?: boolean }) {
  return (
    <a
      className={`brand ${light ? "brand-light" : ""}`}
      href="#top"
      aria-label="Sétima Enfermaria da Santa Casa de Misericórdia do Rio de Janeiro"
    >
      <img
        className="brand-crest"
        src={light ? "/santa-casa-brasao-navbar.png" : "/santa-casa-brasao.png"}
        alt=""
        aria-hidden="true"
      />
      <div>
        <strong>SÉTIMA ENFERMARIA DA SANTA CASA DE MISERICÓRDIA DO RIO DE JANEIRO</strong>
      </div>
    </a>
  );
}

export default function Home() {
  return (
    <div className="ac-site" id="top">
      <header className="topbar">
        <LogoBlock light />
        <nav className="desktop-nav" aria-label="Navegação principal">
          {navItems.map((item) => (
            <a key={item.href} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
        <CtaButton className="topbar-cta">INSCREVA-SE AGORA</CtaButton>
        <details className="mobile-menu">
          <summary aria-label="Abrir menu">
            <Menu size={24} />
          </summary>
          <div>
            {navItems.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
            <a href={whatsappUrl} target="_blank" rel="noreferrer">
              INSCREVA-SE AGORA
            </a>
          </div>
        </details>
      </header>

      <main>
        <section className="hero-section" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="kicker">PÓS-GRADUAÇÃO MÉDICA EM</p>
            <h1 id="hero-title">ACUPUNTURA</h1>
            <div className="gold-rule" />
            <p className="hero-subtitle">
              FORMAÇÃO COMPLETA, CIENTÍFICA E PRÁTICA NA SANTA CASA DO RIO DE JANEIRO
            </p>

            <div className="highlight-grid">
              {highlights.map((item) => {
                const Icon = item.icon;
                return (
                  <article className="highlight-item" key={item.title}>
                    <Icon size={42} strokeWidth={2.1} />
                    <strong>{item.title}</strong>
                    <span>{item.text}</span>
                  </article>
                );
              })}
            </div>

            <CtaButton className="hero-main-cta">QUERO ME INSCREVER</CtaButton>
          </div>
        </section>

        <section className="tradition-strip" aria-label="Tradição e excelência">
          <Building2 className="tradition-icon" size={58} strokeWidth={2.4} aria-hidden="true" />
          <div className="tradition-copy">
            <h2>
              FORMAÇÃO COM <strong>TRADIÇÃO</strong>
            </h2>
            <p>EXCELÊNCIA E REFERÊNCIA EM ENSINO MÉDICO!</p>
            <span>Décadas de compromisso com ciência, prática e cuidado.</span>
          </div>
        </section>

        <section className="coordinator-section" id="coordenacao" aria-labelledby="coord-title">
          <div className="coordinator-photo" aria-hidden="true" />
          <div className="coordinator-content">
            <p className="section-eyebrow">COORDENAÇÃO DO CURSO</p>
            <h2 id="coord-title">Prof. Dr. Aderson Moreira da Rocha</h2>
            <p className="coordinator-lead">
              Médico com sólida trajetória acadêmica e clínica, responsável por conduzir uma
              formação que une evidência científica, prática supervisionada e tradição.
            </p>
            <ul>
              <li>Clínico geral e especialista em Acupuntura pela Associação Médica Brasileira</li>
              <li>Mestre e doutor em Saúde Coletiva pelo Instituto de Medicina Social da UERJ</li>
              <li>Participou de viagens de estudos à China nos anos 1990</li>
              <li>40 anos de estudo da Medicina Chinesa</li>
            </ul>
          </div>
        </section>

        <section className="blue-ribbon" aria-label="Chamada terapêutica">
          <div className="needle-badge" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div>
            <h2>
              ADICIONE ESSA ESPECIALIDADE AO SEU <strong>ARSENAL</strong>
            </h2>
            <p>TERAPÊUTICO E TRANSFORME A MEDICINA!</p>
            <span>Uma formação sólida que une ciência, prática e tradição.</span>
          </div>
        </section>

        <section className="info-section" id="sobre">
          <div className="info-text">
            <article className="reason">
              <div className="circle-icon">
                <Stethoscope size={36} />
              </div>
              <div>
                <h2>PARA QUEM É ESTE CURSO?</h2>
                <p>
                  Médicos que desejam se especializar em Acupuntura com embasamento científico,
                  prática supervisionada e atendimento a pacientes reais.
                </p>
              </div>
            </article>
            <article className="reason">
              <div className="circle-icon">
                <Star size={36} />
              </div>
              <div>
                <h2>POR QUE ESCOLHER A SANTA CASA?</h2>
                <p>
                  Instituição centenária, referência em ensino e assistência à saúde, unindo
                  tradição, excelência e compromisso com a formação médica.
                </p>
              </div>
            </article>
          </div>

          <aside className="signup-card" id="inscricao">
            <h2>VAGAS LIMITADAS!</h2>
            <p>GARANTA A SUA INSCRIÇÃO!</p>
            <div className="seats-box">
              <Users size={44} />
              <strong>VAGAS LIMITADAS</strong>
              <span>PARA A PRIMEIRA TURMA!</span>
            </div>
            <CtaButton>QUERO ME INSCREVER</CtaButton>
          </aside>
        </section>

        <section className="structure-section" id="estrutura" aria-labelledby="estrutura-title">
          <div className="section-title">
            <span />
            <h2 id="estrutura-title">ESTRUTURA DO CURSO</h2>
            <span />
          </div>
          <div className="course-grid">
            {courseCards.map((item) => {
              const Icon = item.icon;
              return (
                <article className="course-card" key={item.title}>
                  <Icon size={48} strokeWidth={1.9} />
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="course-info-section" aria-labelledby="course-info-title">
          <div className="section-title">
            <span />
            <h2 id="course-info-title">INFORMAÇÕES DO CURSO</h2>
            <span />
          </div>

          <div className="course-info-layout">
            <div className="course-info-grid">
              {courseInfo.map((item) => {
                const Icon = item.icon;
                return (
                  <article className="course-info-card" key={item.title}>
                    <Icon size={34} strokeWidth={2} />
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </article>
                );
              })}
            </div>

            <aside className="investment-card">
              <div className="investment-heading">
                <CreditCard size={36} />
                <div>
                  <span>INVESTIMENTO</span>
                  <h3>Condições de pagamento</h3>
                </div>
              </div>
              <div className="investment-values">
                {investmentInfo.map((item) => (
                  <div key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
              <CtaButton>QUERO GARANTIR MINHA VAGA</CtaButton>
            </aside>
          </div>
        </section>

        <section className="testimonials-section" id="depoimentos" aria-labelledby="depoimentos-title">
          <h2 id="depoimentos-title">O QUE NOSSOS ALUNOS DIZEM</h2>
          <div className="testimonial-grid">
            {testimonials.map((item) => (
              <article className="testimonial-card" key={item.name}>
                <Quote size={34} />
                <p>{item.quote}</p>
                <strong>{item.name}</strong>
                <span>{item.role}</span>
              </article>
            ))}
          </div>
        </section>

        <section className="final-cta" id="informacoes">
          <div className="final-needles" aria-hidden="true" />
          <div className="final-copy">
            <h2>DÊ O PRÓXIMO PASSO NA SUA CARREIRA MÉDICA.</h2>
            <p>Transforme vidas com a Acupuntura!</p>
            <CtaButton>INSCREVA-SE AGORA</CtaButton>
          </div>
          <div className="final-benefits">
            <p>
              <Medal size={28} />
              Formação completa e reconhecida
            </p>
            <p>
              <CheckCircle2 size={28} />
              Tradição, ciência e prática
            </p>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-logo">
          <LogoBlock />
        </div>
        <address>
          <MapPin size={24} />
          <span>
            <strong>AMBULATÓRIO DE HOMEOPATIA DA SÉTIMA ENFERMARIA DA SANTA CASA DE MISERICÓRDIA DO RIO DE JANEIRO.</strong>
            Rua Santa Luzia, 206 - Centro - Rio de Janeiro
          </span>
        </address>
        <div className="footer-phones">
          <Phone size={24} />
          <div>
            <a href="tel:+552139235259">(21) 3923-5259</a>
            <a href={whatsappUrl} target="_blank" rel="noreferrer">
              (21) 99140-5512
            </a>
          </div>
        </div>
      </footer>
      <div className="copyright">
        <span>© 2026 Santa Casa de Misericórdia do Rio de Janeiro - Todos os direitos reservados.</span>
        <a href="https://www.serraconsultoria.com" target="_blank" rel="noreferrer">
          Orgulhosamente desenvolvido por Serra Consultoria.
        </a>
      </div>
    </div>
  );
}
